import { useState, useEffect, useRef } from 'react';
import { rooms } from '@/mocks/rooms';

export default function RoomsSection() {
  const [activeRoom, setActiveRoom] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="rooms" ref={sectionRef} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-14 reveal">
          <div className="inline-flex items-center gap-2 border border-gold/50 text-gold rounded-full px-4 py-1.5 mb-4">
            <i className="ri-hotel-bed-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">Rooms &amp; Suites</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-4">
            8 Rooms Across 4 Categories
          </h2>
          <p className="font-sans text-base text-gray-500 max-w-xl mx-auto">
            Each room thoughtfully designed with custom furnishings from precious, sustainably harvested Nicaraguan wood. Rates shown reflect current operating pricing.
          </p>
        </div>

        {/* Rooms Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {rooms.map((room, idx) => (
            <div
              key={room.id}
              className="reveal rounded-2xl overflow-hidden bg-cream border border-cream-dark group cursor-pointer transition-all duration-300 hover:-translate-y-1"
              style={{ transitionDelay: `${idx * 0.07}s` }}
              onClick={() => setActiveRoom(activeRoom === room.id ? null : room.id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setActiveRoom(activeRoom === room.id ? null : room.id)}
            >
              {/* Room Image */}
              <div className="relative overflow-hidden h-52">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-serif text-lg text-white font-medium">{room.name}</p>
                  <p className="font-sans text-xs text-white/75 mt-0.5">{room.beds} · {room.size}</p>
                </div>
              </div>

              {/* Room Details */}
              <div className="p-4">
                <p className={`font-sans text-sm text-gray-600 leading-relaxed transition-all duration-300 ${
                  activeRoom === room.id ? '' : 'line-clamp-2'
                }`}>
                  {room.description}
                </p>

                {/* Amenities */}
                {activeRoom === room.id && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {room.amenities.map(a => (
                      <span key={a} className="bg-forest-50 text-forest text-xs font-sans rounded-full px-2.5 py-1">
                        {a}
                      </span>
                    ))}
                  </div>
                )}

                <button
                  className="mt-3 text-gold text-xs font-sans tracking-wider uppercase flex items-center gap-1 cursor-pointer whitespace-nowrap hover:gap-2 transition-all"
                  onClick={e => { e.stopPropagation(); setActiveRoom(activeRoom === room.id ? null : room.id); }}
                >
                  {activeRoom === room.id ? 'Less Info' : 'View Details'}
                  <i className={`${activeRoom === room.id ? 'ri-arrow-up-line' : 'ri-arrow-right-line'} w-4 h-4 flex items-center justify-center`} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Book CTA */}
        <div className="text-center mt-12 reveal">
          <p className="font-sans text-sm text-gray-500 mb-4">All rooms include en-suite bathroom, AC, high-speed WiFi, and daily housekeeping</p>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-7 py-3 bg-forest text-white font-sans text-sm tracking-widest uppercase rounded-md hover:bg-forest-dark transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
            Submit Investment Inquiry
          </button>
        </div>
      </div>
    </section>
  );
}
